import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { QueryKey, UseQueryOptions } from 'react-query';
import http from '../config/http-service';
import type { Paginated, ProductQuoteHistoryEntry } from './types';
import { restaurantInquiriesEndpoint } from './use-create-restaurant-inquiry-api';

// GET ums/v1/cms/restaurant-inquiries/products/:productId/quote-history —
// cross-inquiry quote timeline for one supplier SKU (the same productId that
// POST .../quotes takes). Pass excludeInquiryId = the inquiry open in the UI
// so its own quotes don't show up as "history". 404 = product deleted.
type TData = Paginated<ProductQuoteHistoryEntry>;
type TError = AxiosError;
type Options = Omit<UseQueryOptions<TData, TError, TData, QueryKey>, 'queryKey' | 'queryFn'>;

export interface ProductQuoteHistoryParams {
  page?: number;
  limit?: number; // backend caps at 100
  excludeInquiryId?: number;
}

export function useGetProductQuoteHistoryApi(
  productId: number | undefined,
  { page = 1, limit = 20, excludeInquiryId }: ProductQuoteHistoryParams = {},
  options?: Options
) {
  return useQueryWrapper<TData, TError>(
    [restaurantInquiriesEndpoint, 'quote-history', productId, { page, limit, excludeInquiryId }],
    async () => {
      const response = await http.get(`${restaurantInquiriesEndpoint}/products/${productId}/quote-history`, {
        // Axios drops undefined params, but excludeInquiryId must also never be
        // sent as a non-numeric value (backend 400s), so callers pass a number.
        params: { page, limit, excludeInquiryId },
      });
      return response.data;
    },
    { enabled: productId != null, ...options }
  );
}
